<template>
  <standard-dialog
    v-model="model"
    :title="$t('supplier_po.send_title')"
    :subtitle="subtitle"
    icon="mdi-send"
    :save-text="channel === 'email' ? $t('supplier_po.send_email') : $t('supplier_po.create_link')"
    save-icon="mdi-send"
    :loading="loading"
    :hide-actions="!!result"
    max-width="560"
    @save="send"
  >
    <!-- Poslije slanja: link se vidi samo ovdje (u bazi je samo hash) -->
    <template v-if="result">
      <v-alert v-if="channel === 'email'" type="success" text dense>
        {{ $t("supplier_po.sent_email_ok", { to: (result.dispatch.recipients || []).join(", ") }) }}
      </v-alert>

      <div class="text-subtitle-2 mb-1">{{ $t("supplier_po.link_ready") }}</div>
      <v-text-field
        :value="result.url"
        readonly
        outlined
        dense
        hide-details
        append-icon="mdi-content-copy"
        @click:append="copy"
        @focus="$event.target.select()"
      />
      <div class="text-caption grey--text text--darken-1 mt-1">
        {{ $t("supplier_po.link_once") }}
      </div>

      <div class="d-flex flex-wrap mt-4" style="gap: 8px">
        <v-btn v-if="channel === 'viber'" color="#7360f2" dark depressed :href="result.share.viber">
          <v-icon left>mdi-phone-message</v-icon>{{ $t("supplier_po.open_viber") }}
        </v-btn>
        <v-btn
          v-if="channel === 'whatsapp'"
          color="#25d366"
          dark
          depressed
          :href="result.share.whatsapp"
          target="_blank"
          rel="noopener"
        >
          <v-icon left>mdi-whatsapp</v-icon>{{ $t("supplier_po.open_whatsapp") }}
        </v-btn>
        <v-btn outlined @click="copy"><v-icon left>mdi-content-copy</v-icon>{{ $t("supplier_po.copy") }}</v-btn>
        <v-spacer />
        <v-btn depressed color="primary" @click="close">{{ $t("supplier_po.close") }}</v-btn>
      </div>
    </template>

    <v-form v-else ref="form" v-model="valid" @submit.prevent="send">
      <v-alert v-if="!document.supplier_id" type="info" text dense>
        {{ $t("supplier_po.no_supplier") }}
      </v-alert>

      <v-btn-toggle v-model="channel" mandatory dense class="mb-4 flex-wrap" color="primary">
        <v-btn value="email"><v-icon left small>mdi-email-outline</v-icon>{{ $t("supplier_po.channel_email") }}</v-btn>
        <v-btn value="viber"><v-icon left small>mdi-phone-message</v-icon>Viber</v-btn>
        <v-btn value="whatsapp"><v-icon left small>mdi-whatsapp</v-icon>WhatsApp</v-btn>
        <v-btn value="link"><v-icon left small>mdi-link-variant</v-icon>{{ $t("supplier_po.channel_link") }}</v-btn>
      </v-btn-toggle>

      <template v-if="channel === 'email'">
        <v-combobox
          v-model="recipients"
          :label="$t('supplier_po.recipients')"
          :hint="$t('supplier_po.recipients_hint')"
          :rules="[recipientsRule]"
          multiple
          chips
          small-chips
          deletable-chips
          outlined
          dense
          persistent-hint
          append-icon=""
          class="mb-2"
        />
        <v-alert v-if="document.supplier_id && !supplierEmail" type="warning" text dense>
          {{ $t("supplier_po.no_supplier_email") }}
        </v-alert>
      </template>

      <v-textarea
        v-model="message"
        :label="$t('supplier_po.message')"
        rows="2"
        auto-grow
        outlined
        dense
        counter="1000"
        maxlength="1000"
      />

      <v-select
        v-model="days"
        :items="dayOptions"
        :label="$t('supplier_po.expires')"
        outlined
        dense
        hide-details
      />
    </v-form>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Slanje narudzbenice dobavljacu: mejl (salje backend) ili link za Viber / WhatsApp / bilo koji
// drugi kanal. Dobavljac odgovara na javnom linku bez prijave.
export default {
  name: "WarehouseSupplierSendDialog",
  components: { StandardDialog },
  mixins: [hasModel, hasSnackbar],
  props: {
    document: { type: Object, required: true },
  },
  data() {
    return {
      channel: "email",
      recipients: [],
      message: "",
      days: 30,
      valid: true,
      loading: false,
      result: null,
    };
  },
  computed: {
    supplierEmail() {
      const s = this.document.supplier;
      return (s && s.email) || "";
    },
    subtitle() {
      const name = this.document.supplier ? this.document.supplier.name : this.document.supplierName;
      return ["#" + (this.document.number || this.document.id), name].filter(Boolean).join(" · ");
    },
    dayOptions() {
      return [7, 14, 30, 60].map((n) => ({ value: n, text: this.$t("supplier_po.days", { n }) }));
    },
  },
  watch: {
    model(open) {
      if (!open) return;
      this.result = null;
      this.channel = "email";
      this.message = "";
      this.days = 30;
      this.recipients = this.supplierEmail ? [this.supplierEmail] : [];
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
  },
  methods: {
    recipientsRule(v) {
      if (this.channel !== "email") return true;
      if (!v || !v.length) return this.$t("supplier_po.recipients_required");
      if (v.length > 5) return this.$t("supplier_po.recipients_max");
      return v.every((e) => EMAIL.test(String(e).trim())) || this.$t("supplier_po.recipients_invalid");
    },
    close() {
      this.model = false;
    },
    async send() {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      this.loading = true;
      try {
        const payload = {
          channel: this.channel,
          message: this.message.trim() || null,
          expires_in_days: this.days,
        };
        if (this.channel === "email") payload.recipients = this.recipients.map((e) => String(e).trim());
        const { data } = await warehouseService.sendDocumentToSupplier(this.document.id, payload);
        this.result = data;
        this.$emit("sent", data);
        if (this.channel === "link") this.copy();
      } catch (e) {
        const res = e.response;
        const first = res && res.data && res.data.errors && Object.values(res.data.errors)[0];
        this.showErrorSnackbar((first && first[0]) || (res && res.data && res.data.message) || this.$t("supplier_po.send_error"));
      } finally {
        this.loading = false;
      }
    },
    async copy() {
      if (!this.result) return;
      try {
        await navigator.clipboard.writeText(this.result.url);
        this.showSuccessSnackbar(this.$t("supplier_po.copied"));
      } catch (e) {
        // clipboard API nije dostupan (http / stari preglednik): polje je selektovano za ručno kopiranje
      }
    },
  },
};
</script>
